import {
  Add,
  CalendarMonth,
  Close,
  Delete,
  Download,
  EventNote,
  ExpandLess,
  ExpandMore,
  List as ListIcon,
  Print,
  Search,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { IResourceComponentsProps } from "@refinedev/core";
import { useDataGrid } from "@refinedev/mui";
import { format, getDay, parse, startOfWeek } from "date-fns";
import { enUS } from "date-fns/locale";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer, Event } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { getClients } from "services/client";
import { deleteAllCredits, deleteCredit, deleteTransaction, payCredit } from "services/credits_v2";
import { AddCreditModal } from "./components/AddCreditModal";
import { CustomToolbar } from "./components/CustomToolbar";
import { EditTransactionDialog } from "./components/EditTransactionDialog";
import { EventInfo } from "./components/EventInfo";
import { FindDay } from "./components/FindDay";

// Configure date-fns
const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

export enum CreditModeEnum {
  calendar,
  list,
}

export interface CreditTransaction {
  id: number;
  amount: number;
  type: 'credit' | 'debit';
  is_paid: boolean;
  paid_amount?: number;
  paid_at?: string;
  paid_by?: number;
  remaining_balance?: number;
  client: {
    id: number;
    name: string;
    pseudo?: string;
  };
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

export interface User {
  id: number;
  username: string;
  email: string;
}

export interface CreditEntry {
  id: number;
  day: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  user: User;
  credit_transactions: CreditTransaction[];
  createdBy: number | null;
  updatedBy: number | null;
}

export interface Client {
  id: number;
  name: string;
}

interface DayTransactions {
  date: string;
  transactions: CreditTransaction[];
}

interface MonthGroup {
  transactions: CreditTransaction[];
  totalAmount: number;
  dates: DayTransactions[];
}

interface MonthData extends MonthGroup {
  month: string;
}

interface GroupedTransactions {
  date: string;
  transactions: CreditTransaction[];
}

interface CreditEvent extends Event {
  id: number;
  title: string;
  start: Date;
  end: Date;
  credit_transactions: CreditTransaction[];
  totalAmount: number;
  originalEntry: any;
}

interface GroupedByDate {
  credit_transactions: CreditTransaction[];
  totalAmount: number;
  originalEntry: any;
}

export const CreditListV2: React.FC<IResourceComponentsProps> = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [deleteTransactionConfirmOpen, setDeleteTransactionConfirmOpen] = useState<boolean>(false);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState<boolean>(false);
  const [selectedCreditId, setSelectedCreditId] = useState<number | null>(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>({});
  const [editTransactionOpen, setEditTransactionOpen] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<CreditTransaction | null>(null);

  const { dataGridProps, tableQueryResult: { refetch } } = useDataGrid();



  const groupedCreditTransactions = useMemo(() => {
    if (!dataGridProps?.rows) return [];

    const grouped: Record<string, CreditTransaction[]> =
      dataGridProps.rows.reduce((acc: Record<string, CreditTransaction[]>, entry: any) => {
        const dateKey = format(new Date(entry.day), 'yyyy-MM-dd');

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }

        acc[dateKey].push(...(entry.credit_transactions || []));

        return acc;
      }, {});

    return Object.entries(grouped)
      .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
      .map(([date, transactions]): GroupedTransactions => ({
        date,
        transactions
      }));
  }, [dataGridProps?.rows]);

  const theme = useTheme();
  const navigate = useNavigate();
  const [date, setDate] = useState<Date | null>(new Date());
  const [openFindDay, setOpenFindDay] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | any | null>(null);
  const [formData, setFormData] = useState({
    date: null as Date | null,
    client_id: null as number | null,
    nbrLines: "",
  });

  const [mode, setMode] = useState<CreditModeEnum>(CreditModeEnum.calendar);

  const { data: dataClients, isLoading: isLoadingClients } = useQuery(
    "clients",
    {
      queryFn: getClients(),
    }
  );

  const events = useMemo(() => {
    if (!dataGridProps?.rows) return [];

    // First, group all entries by date
    const groupedByDate = dataGridProps.rows.reduce((acc: Record<string, GroupedByDate>, entry: any) => {
      const dateKey = new Date(entry.day).toISOString().split('T')[0];

      if (!acc[dateKey]) {
        acc[dateKey] = {
          credit_transactions: [],
          totalAmount: 0,
          originalEntry: entry
        };
      }

      // Map credits to include the original entry's id
      const mappedCredits = entry.credit_transactions.map((credit: CreditTransaction) => ({
        ...credit,
        originalEntryId: entry.id
      }));

      acc[dateKey].credit_transactions.push(...mappedCredits);
      acc[dateKey].totalAmount += mappedCredits.reduce(
        (sum: number, c: CreditTransaction) => sum + (c.is_paid ? 0 : c.amount),
        0
      );

      return acc;
    }, {});

    // Convert grouped data to calendar events
    return Object.entries(groupedByDate).map(([dateStr, data]): CreditEvent => ({
      id: data.originalEntry.id,
      title: `${data.credit_transactions?.length} credit${data.credit_transactions?.length > 1 ? 's' : ''}`,
      start: new Date(dateStr),
      end: new Date(dateStr),
      credit_transactions: data.credit_transactions,
      totalAmount: data.totalAmount,
      originalEntry: data.originalEntry,
      allDay: true,
      resource: null
    }));
  }, [dataGridProps?.rows]);

  const onNavigate = useCallback((newDate: any) => setDate(newDate), [setDate]);

  const handleSelectEvent = (event: Event) => {
    setSelectedEvent(event);
  };

  const handleAddCredit = () => {
    const searchParams = new URLSearchParams();
    if (formData.date) {
      const dateValue =
        formData.date instanceof Date ? formData.date : new Date(formData.date);

      // Use local date string to preserve the intended date
      const localDateString = dateValue.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');

      searchParams.set("date", localDateString);
    } else {
      const currentDate = new Date();
      const localDateString = currentDate.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');

      searchParams.set("date", localDateString);
    }
    if (formData.nbrLines) {
      searchParams.set("nbrLines", formData.nbrLines);
    }

    navigate(`/credit-v2s/create?${searchParams.toString()}`);
    setOpenAddModal(false);
    setFormData({
      date: null,
      client_id: null,
      nbrLines: "",
    });
  };

  // Add a function to handle refetching
  const handleRefetch = async () => {
    await refetch();
    // Also refetch the specific query for the current view
    queryClient.refetchQueries(["credit-v2s"]);
  };

  const paymentMutation = useMutation(
    ({ credit_v2_id, credit_transaction_id, payment_amount }: {
      credit_v2_id: string;
      credit_transaction_id: number;
      payment_amount: number
    }) => payCredit(credit_v2_id, credit_transaction_id, payment_amount)(),
    {
      onSuccess: async () => {
        // Invalidate and refetch all related queries
        await queryClient.invalidateQueries(["credit-v2s"]);
        await handleRefetch();

        // Reset selected event
        setSelectedEvent(null);

        // Show success notification
        enqueueSnackbar('Payment successful', { variant: 'success' });
      },
      onError: (error: any) => {
        // Extract error message
        const errorMessage = error.response?.data?.message || 'Payment failed';

        // Handle specific error scenarios
        if (errorMessage.includes('already paid')) {
          enqueueSnackbar('This transaction has already been paid.', { variant: 'error' });
        } else if (errorMessage.includes('insufficient balance')) {
          enqueueSnackbar('Insufficient balance for this transaction.', { variant: 'warning' });
        } else {
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
      }
    }
  );

  const deleteTransactionMutation = useMutation(deleteTransaction, {
    onSuccess: () => {
      enqueueSnackbar("Transaction deleted successfully", { variant: "success" });
      setDeleteTransactionConfirmOpen(false);
      refetch();
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.data?.error?.message || "Failed to delete transaction", {
        variant: "error",
      });
      setDeleteTransactionConfirmOpen(false);
    },
  });

  const deleteCreditMutation = useMutation(deleteCredit, {
    onSuccess: () => {
      enqueueSnackbar("Credit entry deleted successfully", { variant: "success" });
      setDeleteConfirmOpen(false);
      refetch();
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.data?.error?.message || "Failed to delete credit entry", {
        variant: "error",
      });
      setDeleteConfirmOpen(false);
    },
  });

  const deleteAllCreditsMutation = useMutation(deleteAllCredits, {
    onSuccess: (data) => {
      enqueueSnackbar(data.message || "All credits deleted successfully", { variant: "success" });
      setDeleteAllConfirmOpen(false);
      refetch();
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.data?.error?.message || "Failed to delete all credits", {
        variant: "error",
      });
      setDeleteAllConfirmOpen(false);
    },
  });

  // Add this function to group transactions by month
  const groupedByMonth = useMemo(() => {
    const monthGroups: Record<string, MonthGroup> = groupedCreditTransactions.reduce((acc: Record<string, MonthGroup>, { date, transactions }: GroupedTransactions) => {
      const monthKey = format(new Date(date), "MMMM yyyy");
      if (!acc[monthKey]) {
        acc[monthKey] = {
          transactions: [],
          totalAmount: 0,
          dates: []
        };
      }
      acc[monthKey].transactions.push(...transactions);
      acc[monthKey].totalAmount += transactions.reduce((sum: number, credit: CreditTransaction) =>
        sum + (credit.is_paid ? 0 : credit.amount), 0
      );
      acc[monthKey].dates.push({ date, transactions });
      return acc;
    }, {});

    return Object.entries(monthGroups).map(([month, data]): MonthData => ({
      month,
      ...data
    }));
  }, [groupedCreditTransactions]);

  // Initialize all months as expanded by default
  useEffect(() => {
    if (groupedByMonth.length > 0 && Object.keys(expandedMonths).length === 0) {
      const initialExpandedState: Record<string, boolean> = {};
      // Only expand the most recent month by default
      groupedByMonth.forEach((monthData, index) => {
        initialExpandedState[monthData.month] = index === 0;
      });
      setExpandedMonths(initialExpandedState);
    }
  }, [groupedByMonth]);

  const toggleMonthExpansion = (month: string) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };

  if (isLoadingClients) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      mt={2}
      mb={2}
      component="main"
      sx={{
        flexGrow: 1,
        px: 0,
        width: "100%",
      }}
    >
      <Box>
        <Card>
          <CardHeader
            title="Credis"
            subheader="Manage your credits easily"
          />
          <Divider />
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, newMode) => newMode !== null && setMode(newMode)}
                aria-label="view mode"
                sx={{ mr: 2 }}
              >
                <ToggleButton value={CreditModeEnum.calendar} aria-label="calendar view">
                  <CalendarMonth />
                </ToggleButton>
                <ToggleButton value={CreditModeEnum.list} aria-label="list view">
                  <ListIcon />
                </ToggleButton>
              </ToggleButtonGroup>

              <ButtonGroup
                size="large"
                variant="contained"
                aria-label="outlined primary button group"
              >
                <Button
                  onClick={() => setOpenAddModal(true)}
                  size="large"
                  variant="contained"
                  startIcon={<Add />}
                >
                  Add New Credit
                </Button>
                <Button
                  onClick={() => setOpenFindDay(true)}
                  size="large"
                  variant="contained"
                  startIcon={<Search />}
                >
                  Find Day
                </Button>
              </ButtonGroup>
            </Box>
            <Divider style={{ margin: 10 }} />

            <AddCreditModal
              open={openAddModal}
              handleClose={() => setOpenAddModal(false)}
              formData={formData}
              setFormData={setFormData}
              onAddCredit={handleAddCredit}
              clients={dataClients || []}
            />

            <FindDay
              date={date}
              open={openFindDay}
              handleClose={() => {
                setOpenFindDay(false);
                setDate(new Date());
              }}
              onSetDate={(date) => {
                setDate(date);
                setOpenFindDay(false);
              }}
            />

            {mode === CreditModeEnum.calendar ? (
              <Calendar
                localizer={localizer}
                events={events}
                onSelectEvent={handleSelectEvent}
                selectable
                startAccessor="start"
                endAccessor="end"
                defaultView="month"
                view="month"
                views={["month", "week"]}
                date={date ? date : new Date()}
                onNavigate={onNavigate}
                components={{
                  event: EventInfo,
                  toolbar: CustomToolbar,
                }}
                eventPropGetter={(event: any) => ({
                  style: {
                    backgroundColor:
                      selectedEvent?.id === event?.id
                        ? theme.palette.primary.main
                        : theme.palette.primary.light,
                    borderColor: theme.palette.primary.main,
                  },
                })}
                style={{
                  height: 900,
                  padding: 0,
                }}
              />
            ) : (
              <Box sx={{ width: "100%" }}>
                {groupedByMonth.map(({ month, dates, totalAmount }) => (
                  <Box key={month} sx={{ mb: 4 }}>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 3,
                        mb: expandedMonths[month] ? 2 : 0,
                        backgroundColor: alpha(theme.palette.primary.main, 0.03),
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        }
                      }}
                      onClick={() => toggleMonthExpansion(month)}
                    >
                      <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {expandedMonths[month] ?
                            <ExpandLess color="primary" fontSize="medium" /> :
                            <ExpandMore color="primary" fontSize="medium" />}
                          <Typography variant="h4" sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main
                          }}>
                            {month}
                          </Typography>
                        </Box>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2
                        }}>
                          <Chip
                            label={`${dates.reduce((sum, { transactions }) => sum + transactions.length, 0)} Credits`}
                            color="primary"
                            sx={{
                              borderRadius: 2,
                              px: 2,
                              '& .MuiChip-label': {
                                fontWeight: 600
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Typography variant="h5" sx={{
                            fontWeight: 700,
                            color: theme.palette.success.main,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            {totalAmount.toLocaleString()} TND
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {expandedMonths[month] && dates.map(({ date, transactions }) => (
                      <Paper
                        key={date}
                        sx={{
                          mb: 2,
                          p: 3,
                          borderRadius: 2,
                          boxShadow: theme.shadows[2],
                          '&:hover': {
                            boxShadow: theme.shadows[4],
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease'
                          }
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                          <Typography variant="h6" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 600
                          }}>
                            {format(new Date(date), "EEEE, dd MMMM")}
                          </Typography>
                          <Chip
                            label={`${transactions.length} Credit${transactions.length > 1 ? "s" : ""}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {transactions.map((credit: CreditTransaction, idx: number) => {
                            console.log({ credit });

                            return (
                              <Paper
                                key={idx}
                                elevation={0}
                                onClick={() => {
                                  if (!credit.is_paid) {
                                    setSelectedTransaction(credit);
                                    setEditTransactionOpen(true);
                                  }
                                }}
                                sx={{
                                  p: 2.5,
                                  cursor: 'pointer',
                                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                  borderRadius: 2,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                                  backdropFilter: 'blur(8px)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    transform: 'translateX(4px)',
                                    backgroundColor: alpha(theme.palette.background.paper, 0.9)
                                  }
                                }}
                              >
                                <Box sx={{ flex: 1, cursor: 'pointer' }}>
                                  <Typography variant="subtitle1" sx={{
                                    fontWeight: 600,
                                    color: theme.palette.text.primary
                                  }}>
                                    {credit?.client?.name || "Unknown Client"}{credit?.client?.pseudo ? ` (${credit?.client?.pseudo})` : ""}
                                  </Typography>
                                  <Typography variant="caption" sx={{
                                    color: theme.palette.text.secondary,
                                    display: 'block'
                                  }}>
                                    ID: {credit?.client?.id}
                                  </Typography>
                                </Box>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 3,
                                  flex: 1,
                                  justifyContent: 'flex-end'
                                }}>
                                  <Typography variant="h6" sx={{
                                    fontWeight: 600,
                                    color: credit.amount > 500000 ? theme.palette.info.main : theme.palette.warning.main
                                  }}>
                                    {credit?.amount?.toLocaleString()} TND
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      variant="contained"
                                      color={credit.is_paid ? "secondary" : "success"}
                                      disabled={credit.is_paid}
                                      size="small"
                                      sx={{
                                        minWidth: 100,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600
                                      }}
                                      onClick={() => {
                                        const originalEntry = dataGridProps.rows.find((entry: any) =>
                                          entry.credit_transactions.some((t: CreditTransaction) => t.id === credit.id)
                                        );
                                        paymentMutation.mutate({
                                          credit_v2_id: originalEntry.id,
                                          credit_transaction_id: credit.id,
                                          payment_amount: credit.amount
                                        });
                                      }}
                                    >
                                      {credit.is_paid ? "Paid" : "Pay Now"}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      sx={{
                                        minWidth: 40,
                                        borderRadius: 2,
                                      }}
                                      onClick={() => {
                                        setSelectedTransactionId(credit.id);
                                        setDeleteTransactionConfirmOpen(true);
                                      }}
                                    >
                                      <Delete fontSize="small" />
                                    </Button>
                                  </Box>
                                </Box>
                              </Paper>
                            )
                          })}
                        </Box>
                        <Box sx={{
                          mt: 3,
                          pt: 2,
                          borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Delete />}
                            onClick={() => {
                              const originalEntry = dataGridProps.rows.find((entry: any) =>
                                entry.day === date
                              );
                              if (originalEntry) {
                                setSelectedCreditId(originalEntry.id);
                                setDeleteConfirmOpen(true);
                              }
                            }}
                          >
                            Delete All
                          </Button>
                          <Typography variant="subtitle1" sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500
                          }}>
                            Daily Total
                          </Typography>
                          <Typography variant="h6" sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main
                          }}>
                            {transactions.reduce((sum: number, credit: CreditTransaction) =>
                              sum + (credit.is_paid ? 0 : credit.amount), 0
                            )?.toLocaleString()} TND
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "600px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventNote color="primary" />
            <Typography variant="h6">Credit Details</Typography>
          </Box>
          <IconButton onClick={() => setSelectedEvent(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: 3 }}>
          {selectedEvent && (
            <>
              <Box
                sx={{
                  mb: 4,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: alpha(theme.palette.primary.light, 0.1),
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Date
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#0B192C",
                    }}
                  >
                    {format(selectedEvent.start!, "EEEE, dd MMMM yyyy")}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 1,
                  }}
                >
                  <Chip
                    label={`${selectedEvent.credit_transactions?.length} Credit${selectedEvent.credit_transactions?.length > 1 ? "s" : ""
                      }`}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: 1,
                      "& .MuiChip-label": {
                        px: 2,
                        fontWeight: 500,
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Reference #{selectedEvent.id}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  maxHeight: "400px",
                  overflowY: "auto",
                  pr: 1,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    borderRadius: "3px",
                  },
                }}
              >
                {selectedEvent?.credit_transactions!.map((credit: CreditTransaction, idx: number) => {
                  const client = dataClients?.find(
                    (c: Client) => c.id === credit.client?.id
                  ) as any;
                  const amount = credit.amount;

                  return (
                    <Paper
                      key={idx}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderRadius: 2,
                        border: `1px solid ${alpha(
                          theme.palette.divider,
                          0.8
                        )}`,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          cursor: "pointer",
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.03
                          ),
                          borderColor: theme.palette.primary.main,
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <Box sx={{ cursor: 'pointer' }} onClick={() => {
                        if (!credit.is_paid) {
                          setSelectedTransaction(credit);
                          setEditTransactionOpen(true);
                        }
                      }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            mb: 0.5,
                          }}
                        >
                          {client?.name || "Unknown Client"}{client?.pseudo ? ` (${client?.pseudo})` : ""}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          Client ID: {credit.client?.id}
                        </Typography>
                      </Box>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        textAlign: "right"
                      }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: amount > 500000 ? "#4A628A" : "#F87A53 ",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {credit.amount.toLocaleString()} TND
                        </Typography>
                        <Button
                          variant="contained"
                          color={credit.is_paid ? "secondary" : "success"}
                          size="small"
                          disabled={credit.is_paid}
                          onClick={() => {
                            paymentMutation.mutate({
                              credit_v2_id: selectedEvent?.id,
                              credit_transaction_id: credit?.id,
                              payment_amount: credit?.amount
                            });
                          }}

                        >
                          {credit.is_paid ? "Paid" : "Pay Now"}
                        </Button>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>

              <Box
                sx={{
                  mt: 4,
                  pt: 3,
                  borderTop: `2px solid ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  Total Amount
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.dark,
                    letterSpacing: "-0.5px",
                  }}
                >
                  {selectedEvent.credit_transactions
                    .filter((credit: any) => !credit.is_paid)
                    .reduce((sum: any, credit: any) => sum + credit.amount, 0)?.toLocaleString()
                  } TND
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      {/* Delete Transaction Confirmation Dialog */}
      <Dialog
        open={deleteTransactionConfirmOpen}
        onClose={() => setDeleteTransactionConfirmOpen(false)}
        aria-labelledby="delete-transaction-dialog-title"
      >
        <DialogTitle id="delete-transaction-dialog-title">
          Confirm Delete Transaction
          <IconButton
            aria-label="close"
            onClick={() => setDeleteTransactionConfirmOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: 2, pb: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to delete this transaction? This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setDeleteTransactionConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (selectedTransactionId) {
                  deleteTransactionMutation.mutate(selectedTransactionId);
                }
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Delete Credit Entry Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-credit-dialog-title"
      >
        <DialogTitle id="delete-credit-dialog-title">
          Confirm Delete Credit Entry
          <IconButton
            aria-label="close"
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: 2, pb: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to delete this entire credit entry and all its transactions? This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (selectedCreditId) {
                  deleteCreditMutation.mutate(selectedCreditId);
                }
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Delete All Credits Confirmation Dialog */}
      <Dialog
        open={deleteAllConfirmOpen}
        onClose={() => setDeleteAllConfirmOpen(false)}
        aria-labelledby="delete-all-credits-dialog-title"
      >
        <DialogTitle id="delete-all-credits-dialog-title">
          Confirm Delete All Credits
          <IconButton
            aria-label="close"
            onClick={() => setDeleteAllConfirmOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: 2, pb: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }} color="error.main" fontWeight="bold">
            WARNING: This will delete ALL your credit entries and transactions!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to delete all your credit entries and their transactions? This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setDeleteAllConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => deleteAllCreditsMutation.mutate()}
            >
              Delete All
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <EditTransactionDialog
        open={editTransactionOpen}
        onClose={() => {
          setEditTransactionOpen(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
        onSuccess={() => {
          refetch();
        }}
      />
    </Box>
  );
};
